import { Component, DestroyRef, OnInit, computed, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { MultiSelectModule } from 'primeng/multiselect';
import { ToastModule } from 'primeng/toast';
import { Observable, finalize, from, map, switchMap } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LocalizationService } from '../../core/localization/localization.service';
import {
  AiAssistAppliedEvent,
  AiAssistDiscardFn,
  AiAssistGenerateFn,
  AiAssistGenerateRequest,
  AiAssistResult
} from '../../shared/ai-assist/ai-assist.models';
import { hasAiAssistPermission } from '../../shared/ai-assist/ai-assist.util';
import { AinAiAssistShellComponent } from '../../shared/ai-assist/ain-ai-assist-shell.component';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { KnowledgeSourceSelectorComponent } from '../../shared/design-system/knowledge-source-selector.component';
import { GrowthContentAssetEditorComponent } from './growth-content-asset-editor.component';
import {
  GrowthContentAssetEditorState,
  buildUpsertGrowthContentAssetPayloadFromEditor,
  createDefaultGrowthContentAssetEditorState,
  growthContentAssetEditorStateFromAsset
} from './growth-content-asset-editor.util';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { StructuredJsonFieldDef } from '../../shared/design-system/structured-json-form.model';
import { GrowthAgentService, GrowthStrategyArtifact } from './growth-agent.service';
import {
  applyGrowthContentAssistFieldPatches,
  buildGrowthContentAssistClientSnapshot,
  computeGrowthContentAssistSourceHash,
  mapGrowthContentAssistDraftToFieldPatches
} from './growth-content-asset-ai-assist.util';
import {
  GrowthContentAssistClientSnapshot,
  GrowthContentAssistDraftResponse,
  GrowthContentAsset,
  GrowthMarketingService
} from './growth-marketing.service';
import {
  CONTENT_STATUS_I18N_KEYS,
  normalizeContentStatus,
  strategyArtifactTypeI18nKey
} from './growth-status.util';

const AI_ASSIST_PERMISSION = 'growth.content.ai.assist';

@Component({
  standalone: true,
  selector: 'ain-growth-content-asset-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    AinFormFieldComponent,
    AinAiAssistShellComponent,
    InfoHintComponent,
    GrowthContentAssetEditorComponent,
    KnowledgeSourceSelectorComponent,
    FormsModule,
    RouterLink,
    ButtonModule,
    MultiSelectModule,
    ToastModule
  ],
  providers: [MessageService],
  template: `
    <p-toast />
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="pageTitle()"
      [description]="t('growth.contentAsset.detail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.contentAsset.help.page.title'"
        [descriptionKey]="'growth.contentAsset.help.page.description'"
        [bulletKeys]="[
          'growth.contentAsset.help.page.bullets.basics',
          'growth.contentAsset.help.page.bullets.body',
          'growth.contentAsset.help.page.bullets.seo',
          'growth.contentAsset.help.page.bullets.citations',
          'growth.contentAsset.help.page.bullets.publishTargets'
        ]" />

      <div class="toolbar">
        <a
          pButton
          routerLink="/growth/marketing/article-publishing"
          severity="secondary"
          [label]="t('common.back')"></a>
        @if (!isNew() && asset()) {
          <span class="status-chip">{{ contentStatusLabel(asset()!.status) }}</span>
          <a
            pButton
            [routerLink]="['/growth/marketing/article-publishing', asset()!.id]"
            [label]="t('growth.contentAsset.next.toReview')"></a>
          <a
            pButton
            severity="secondary"
            routerLink="/growth/marketing/ace-studio"
            [label]="t('growth.articlePublishing.actions.openAceStudio')"></a>
          <a
            pButton
            severity="help"
            [outlined]="true"
            [routerLink]="['/growth/marketing/publish-operations']"
            [queryParams]="{ contentId: asset()!.id, channel: 'web-blog' }"
            [label]="t('growth.articlePublishing.actions.openPublishOps')"></a>
        }
        @if (canUseAiAssist()) {
          <button
            pButton
            type="button"
            icon="pi pi-sparkles"
            severity="secondary"
            [label]="isNew() ? t('growth.contentAsset.aiAssist.actions.generate') : t('growth.contentAsset.aiAssist.actions.edit')"
            [disabled]="loading() || saving()"
            (click)="openAiAssist()"></button>
          @if (hasBodyForImprove()) {
            <button
              pButton
              type="button"
              icon="pi pi-verified"
              severity="help"
              [outlined]="true"
              [label]="t('growth.contentAsset.aiAssist.actions.improve')"
              [disabled]="loading() || saving()"
              (click)="openAiAssistImprove()"></button>
          }
        }
        <button
          pButton
          type="button"
          [label]="isNew() ? t('common.create') : t('common.save')"
          [loading]="saving()"
          [disabled]="!canSave() || saving()"
          (click)="save()"></button>
      </div>
      @if (pendingAiAssistDraftId) {
        <div class="ai-draft-banner">{{ t('growth.contentAsset.aiAssist.pendingDraftHint') }}</div>
      }

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.error')" [message]="error() ?? ''" />
      } @else {
        <ain-growth-content-asset-editor [state]="editorState" (stateChange)="onEditorStateChange($event)" />
      }

      <ain-ai-assist-shell
        #aiAssistShell
        [mode]="aiAssistShellMode()"
        [titleKey]="aiAssistTitleKey()"
        [infoTitleKey]="'growth.contentAsset.aiAssist.info.title'"
        [infoDescriptionKey]="'growth.contentAsset.aiAssist.info.description'"
        [infoBulletKeys]="[
          'growth.contentAsset.aiAssist.info.bullets.review',
          'growth.contentAsset.aiAssist.info.bullets.grounding'
        ]"
        [infoPreviewTitleKey]="'growth.contentAsset.aiAssist.preview.title'"
        [infoPreviewDescriptionKey]="'growth.contentAsset.aiAssist.preview.description'"
        [structuredFieldDefs]="aiAssistStructuredFields()"
        [showPromptTextarea]="aiAssistShowPrompt()"
        [requireInstruction]="aiAssistRequireInstruction()"
        [promptLabelKey]="'growth.contentAsset.aiAssist.prompt.label'"
        [promptHintKey]="'growth.contentAsset.aiAssist.prompt.hint'"
        [promptPlaceholderKey]="'growth.contentAsset.aiAssist.prompt.placeholder'"
        [permissionKey]="aiAssistPermissionKey"
        [generate]="generateAiAssist"
        [discard]="discardAiAssist"
        (applied)="onAiAssistApplied($event)">
        <div aiAssistContext class="ai-context-panel">
          <p class="ai-context-note">{{ t('growth.contentAsset.aiAssist.context.note') }}</p>
          <ain-form-field [label]="t('growth.contentAsset.aiAssist.context.strategyArtifacts')">
            <p-multiSelect
              [options]="strategyArtifactOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="selectedStrategyArtifactIds"
              [filter]="true"
              display="chip"
              appendTo="body"
              [placeholder]="t('growth.contentAsset.aiAssist.context.strategyArtifactsPlaceholder')" />
          </ain-form-field>
          <ain-form-field [label]="t('growth.contentAsset.aiAssist.context.knowledgeSource')">
            <ain-knowledge-source-selector [(ngModel)]="selectedKnowledgeSourceId" statusFilter="Active" />
          </ain-form-field>
        </div>
      </ain-ai-assist-shell>
    </ain-page>
  `,
  styles: [`
    .toolbar {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
      align-items: center;
      margin-bottom: 16px;
    }

    .ai-draft-banner {
      margin-bottom: 12px;
      padding: 10px 12px;
      border-radius: 10px;
      border: 1px solid #bfdbfe;
      background: #eff6ff;
      color: #1e3a8a;
      font-size: 0.9rem;
    }

    .ai-context-panel {
      display: grid;
      gap: 12px;
    }

    .ai-context-note {
      margin: 0;
      color: #475569;
      font-size: 0.9rem;
      line-height: 1.5;
    }

    .status-chip {
      border: 1px solid #d1d5db;
      border-radius: 999px;
      padding: 0.2rem 0.6rem;
      font-size: 0.85rem;
    }

  `]
})
export class GrowthContentAssetDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly service = inject(GrowthMarketingService);
  private readonly agentService = inject(GrowthAgentService);
  private readonly i18n = inject(LocalizationService);
  private readonly contextService = inject(CurrentContextService);
  private readonly messages = inject(MessageService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly aiAssistShell = viewChild<AinAiAssistShellComponent>('aiAssistShell');

  readonly aiAssistPermissionKey = AI_ASSIST_PERMISSION;

  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly error = signal<string | null>(null);
  readonly isNew = signal(true);
  readonly asset = signal<GrowthContentAsset | null>(null);
  readonly strategyArtifacts = signal<GrowthStrategyArtifact[]>([]);

  readonly canUseAiAssist = computed(() =>
    hasAiAssistPermission(this.contextService.context(), AI_ASSIST_PERMISSION)
  );

  editorState: GrowthContentAssetEditorState = createDefaultGrowthContentAssetEditorState();

  pendingAiAssistDraftId: string | null = null;
  selectedStrategyArtifactIds: string[] = [];
  selectedKnowledgeSourceId: string | null = null;

  readonly generateAiAssist: AiAssistGenerateFn = payload => this.runAiAssistGenerate(payload);
  readonly discardAiAssist: AiAssistDiscardFn = draftId => this.service.discardContentAssistDraft(draftId);

  readonly aiAssistIntent = signal<'generate' | 'improve'>('generate');

  readonly aiAssistShellMode = computed((): 'create' | 'edit' =>
    this.aiAssistIntent() === 'improve' ? 'edit' : this.isNew() ? 'create' : 'edit'
  );

  readonly aiAssistTitleKey = computed(() => {
    if (this.aiAssistIntent() === 'improve') {
      return 'growth.contentAsset.aiAssist.title.improve';
    }
    return this.isNew()
      ? 'growth.contentAsset.aiAssist.title.create'
      : 'growth.contentAsset.aiAssist.title.edit';
  });

  readonly aiAssistShowPrompt = computed(
    () => this.aiAssistIntent() === 'improve' || !this.isNew()
  );

  readonly aiAssistRequireInstruction = computed(
    () => this.aiAssistIntent() === 'improve' || !this.isNew()
  );

  ngOnInit(): void {
    this.agentService.listStrategyArtifacts().subscribe({
      next: items => this.strategyArtifacts.set(items ?? []),
      error: () => this.strategyArtifacts.set([])
    });

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const contentId = params.get('contentId');
      if (!contentId || contentId === 'new') {
        this.isNew.set(true);
        this.asset.set(null);
        this.resetNewForm();
        this.loading.set(false);
        this.error.set(null);
        return;
      }

      this.isNew.set(false);
      this.loadContent(contentId);
    });
  }

  aiAssistStructuredFields(): StructuredJsonFieldDef[] {
    if (this.aiAssistIntent() === 'improve' || !this.isNew()) {
      return [];
    }

    return [
      { key: 'topic', type: 'string', labelKey: 'growth.contentAsset.aiAssist.fields.topic', required: true },
      { key: 'goal', type: 'string', labelKey: 'growth.contentAsset.aiAssist.fields.goal' },
      { key: 'audience', type: 'string', labelKey: 'growth.contentAsset.aiAssist.fields.audience' },
      { key: 'tone', type: 'string', labelKey: 'growth.contentAsset.aiAssist.fields.tone' },
      {
        key: 'length',
        type: 'select',
        labelKey: 'growth.contentAsset.aiAssist.fields.length',
        options: [
          { value: 'short', labelKey: 'growth.contentAsset.aiAssist.length.short' },
          { value: 'medium', labelKey: 'growth.contentAsset.aiAssist.length.medium' },
          { value: 'long', labelKey: 'growth.contentAsset.aiAssist.length.long' }
        ]
      },
      { key: 'keywords', type: 'string', labelKey: 'growth.contentAsset.aiAssist.fields.keywords' },
      { key: 'cta', type: 'string', labelKey: 'growth.contentAsset.aiAssist.fields.cta' },
      { key: 'constraints', type: 'textarea', labelKey: 'growth.contentAsset.aiAssist.fields.constraints' },
      {
        key: 'bodyFormat',
        type: 'select',
        labelKey: 'growth.contentAsset.aiAssist.fields.bodyFormat',
        options: [
          { value: 'markdown', labelKey: 'growth.contentAsset.fields.bodyFormat.markdown' },
          { value: 'html', labelKey: 'growth.contentAsset.fields.bodyFormat.html' }
        ]
      }
    ];
  }

  strategyArtifactOptions(): Array<{ value: string; label: string }> {
    return this.strategyArtifacts().map(item => ({
      value: item.id,
      label: `${item.title} (${this.t(strategyArtifactTypeI18nKey(item.artifactType))})`
    }));
  }

  onEditorStateChange(state: GrowthContentAssetEditorState): void {
    this.editorState = state;
  }

  hasBodyForImprove(): boolean {
    return (this.editorState.model.bodyMarkdown ?? '').trim().length > 0;
  }

  openAiAssist(): void {
    this.aiAssistIntent.set('generate');
    this.aiAssistShell()?.open({
      instruction: '',
      structuredAnswers: this.isNew()
        ? {
            topic: this.editorState.model.title.trim() || '',
            goal: '',
            audience: '',
            tone: '',
            length: 'medium',
            keywords: '',
            cta: '',
            constraints: '',
            bodyFormat: this.editorState.model.bodyFormat
          }
        : {},
      contextSelections: this.buildContextSelections()
    });
  }

  openAiAssistImprove(): void {
    this.aiAssistIntent.set('improve');
    this.aiAssistShell()?.open({
      instruction: this.t('growth.contentAsset.aiAssist.improve.defaultInstruction'),
      structuredAnswers: {
        length: 'long',
        bodyFormat: this.editorState.model.bodyFormat
      },
      contextSelections: this.buildContextSelections()
    });
  }

  onAiAssistApplied(event: AiAssistAppliedEvent): void {
    this.editorState = applyGrowthContentAssistFieldPatches(this.editorState, event.patches);
    this.pendingAiAssistDraftId = event.draftId?.trim() || null;

    if (event.result.usedFallback) {
      this.messages.add({
        severity: 'warn',
        summary: this.t('growth.contentAsset.aiAssist.fallbackApplied')
      });
    }

    const warnings = event.result.warnings ?? [];
    const coverUnavailable = warnings.some(item =>
      item.includes('growth_content_assist_cover_unavailable')
    );
    if (coverUnavailable) {
      this.messages.add({
        severity: 'warn',
        summary: this.t('growth.contentAsset.aiAssist.coverUnavailable.title'),
        detail: this.t('growth.contentAsset.aiAssist.coverUnavailable.detail'),
        life: 12000
      });
    }

    const bodyTooShort = warnings.some(
      item =>
        item.includes('growth_content_assist_body_too_short') ||
        item.toLowerCase().includes('shorter than the requested length')
    );
    if (bodyTooShort) {
      this.messages.add({
        severity: 'warn',
        summary: this.t('growth.contentAsset.aiAssist.bodyTooShort.title'),
        detail: this.t('growth.contentAsset.aiAssist.bodyTooShort.detail'),
        life: 12000
      });
    } else if (
      warnings.some(
        item =>
          !item.includes('growth_content_assist_cover_unavailable') &&
          !item.toLowerCase().includes('cover image could not be generated')
      )
    ) {
      this.messages.add({
        severity: 'info',
        summary: this.t('growth.contentAsset.aiAssist.appliedWithWarnings'),
        detail: warnings.slice(0, 3).join(' · '),
        life: 8000
      });
    }
  }

  pageTitle(): string {
    return this.isNew()
      ? this.t('growth.contentAsset.detail.titleNew')
      : this.t('growth.contentAsset.detail.titleEdit');
  }

  canSave(): boolean {
    return this.editorState.model.title.trim().length > 0;
  }

  save(): void {
    if (!this.canSave()) {
      return;
    }

    const payload = buildUpsertGrowthContentAssetPayloadFromEditor(this.editorState, {
      id: this.asset()?.id ?? null,
      aiAssistDraftId: this.pendingAiAssistDraftId
    });

    const request$ = this.isNew()
      ? this.service.createContent(payload)
      : this.service.updateContent(this.asset()!.id, payload);

    this.saving.set(true);
    this.error.set(null);
    request$.pipe(finalize(() => this.saving.set(false))).subscribe({
      next: saved => {
        this.pendingAiAssistDraftId = null;
        if (this.isNew()) {
          void this.router.navigate(['/growth/marketing/content-assets', saved.id]);
          return;
        }
        this.reloadContent(saved.id);
      },
      error: () => this.error.set(this.t('common.error'))
    });
  }

  contentStatusLabel(status: unknown): string {
    return this.t(CONTENT_STATUS_I18N_KEYS[normalizeContentStatus(status)]);
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }

  private loadContent(contentId: string): void {
    this.loading.set(true);
    this.error.set(null);
    this.service.getContent(contentId).pipe(finalize(() => this.loading.set(false))).subscribe({
      next: asset => this.bindAsset(asset),
      error: () => {
        this.asset.set(null);
        this.error.set(this.t('common.notFound'));
      }
    });
  }

  private reloadContent(contentId: string): void {
    this.service.getContent(contentId).subscribe({
      next: asset => this.bindAsset(asset),
      error: () => this.error.set(this.t('common.error'))
    });
  }

  private bindAsset(asset: GrowthContentAsset): void {
    this.isNew.set(false);
    this.asset.set(asset);
    this.editorState = growthContentAssetEditorStateFromAsset(asset);
    this.pendingAiAssistDraftId = null;
  }

  private resetNewForm(): void {
    this.editorState = createDefaultGrowthContentAssetEditorState();
    this.pendingAiAssistDraftId = null;
    this.selectedStrategyArtifactIds = [];
    this.selectedKnowledgeSourceId = null;
  }

  private runAiAssistGenerate(payload: AiAssistGenerateRequest): Observable<AiAssistResult> {
    const snapshot = buildGrowthContentAssistClientSnapshot(this.editorState);
    const baseVersion = this.isNew() ? null : (this.asset()?.version ?? null);
    const structuredAnswersJson = JSON.stringify(payload.structuredAnswers ?? {});
    const bodyFormatFromAnswers =
      typeof payload.structuredAnswers?.['bodyFormat'] === 'string'
        ? String(payload.structuredAnswers['bodyFormat'])
        : this.editorState.model.bodyFormat;

    return from(computeGrowthContentAssistSourceHash(snapshot, baseVersion)).pipe(
      switchMap(sourceHash => {
        const common = {
          actionKey: this.isNew()
            ? 'growth.content.asset.generate-draft'
            : 'growth.content.asset.transform-draft',
          instruction: payload.instruction || null,
          structuredAnswersJson,
          contextSelections: this.buildContextSelections(),
          clientSnapshot: snapshot,
          sourceHash,
          fieldScopes: this.isNew() ? null : ['title', 'summary', 'body', 'seo'],
          bodyFormat: bodyFormatFromAnswers,
          applyMode: 'ReturnDraftOnly',
          idempotencyKey: crypto.randomUUID(),
          correlationId: crypto.randomUUID(),
          invocationSource: 'AdminUi'
        };

        const request$ = this.isNew()
          ? this.service.generateContentAssistDraft(common)
          : this.service.transformContentAssistDraft(this.asset()!.id, {
              ...common,
              baseVersion
            });

        return request$.pipe(
          map(response =>
            this.mapAssistResponseToResult(response, snapshot)
          )
        );
      })
    );
  }

  private buildContextSelections() {
    return {
      strategyArtifactIds: this.selectedStrategyArtifactIds.length ? [...this.selectedStrategyArtifactIds] : null,
      knowledgeSourceIds: this.selectedKnowledgeSourceId ? [this.selectedKnowledgeSourceId] : null,
      briefId: this.asset()?.briefId ?? null,
      offeringId: null,
      segmentId: null,
      storeKey: null
    };
  }

  private mapAssistResponseToResult(
    response: GrowthContentAssistDraftResponse,
    before: GrowthContentAssistClientSnapshot
  ): AiAssistResult {
    const patches = mapGrowthContentAssistDraftToFieldPatches(
      response,
      before,
      this.editorState.model.slug ?? '',
      this.editorState.seo
    );

    return {
      draftId: response.draftId,
      patches,
      warnings: response.warnings ?? [],
      usedFallback: !!response.usedFallback,
      correlationId: response.correlationId,
      sourceHash: response.sourceHash
    };
  }
}
